import type { Address } from '@ahm/api-wrappers-core';
import { create } from 'zustand';
import { createSelectors } from './selectors';

/**
 * Store nhỏ cho tính năng "nhắc đổi địa chỉ theo GPS".
 *
 * - Chạy dò GPS 1 lần/phiên (xem hooks/use-location-mismatch-detect).
 * - Kết quả {status, gpsAddress, distance} dùng chung cho popover ở store-header
 *   (trang chủ) và order-location-trigger (giỏ hàng).
 * - `pendingPrefill` là kênh truyền địa chỉ GPS đã reverse-geocode sang picker khi
 *   user bấm "Đổi địa chỉ" — picker consume 1 lần rồi clear.
 * - Cờ "đã nhắc" lưu ở sessionStorage → tối đa 1 lần/phiên (dùng chung home + cart).
 */

const SESSION_KEY = 'ahafood.location_reminder_handled';

export type LocationMismatchStatus =
  | 'idle'
  | 'checking'
  | 'mismatch'
  | 'dismissed'
  | 'skipped';

type LocationMismatchState = {
  status: LocationMismatchStatus;
  gpsAddress: Address | null;
  distance: number | null;
  pendingPrefill: Address | null;
};

type LocationMismatchActions = {
  startChecking: () => void;
  setMismatch: (gpsAddress: Address, distance: number) => void;
  setSkipped: () => void;
  dismiss: () => void;
  setPendingPrefill: (address: Address | null) => void;
  /** Trả pendingPrefill và xoá khỏi store (dùng đúng 1 lần). */
  consumePendingPrefill: () => Address | null;
};

const readHandled = (): boolean => {
  try {
    return (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(SESSION_KEY) === '1'
    );
  } catch {
    return false;
  }
};

const writeHandled = (): void => {
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // ignore
  }
};

const store = create<LocationMismatchState & LocationMismatchActions>(
  (set, get) => ({
    status: readHandled() ? 'dismissed' : 'idle',
    gpsAddress: null,
    distance: null,
    pendingPrefill: null,

    startChecking: () => {
      if (get().status === 'idle') {
        set({ status: 'checking' });
      }
    },

    setMismatch: (gpsAddress, distance) => {
      // Tôn trọng cờ đã-nhắc trong phiên này.
      if (readHandled()) {
        set({ status: 'dismissed' });
        return;
      }
      set({ status: 'mismatch', gpsAddress, distance });
    },

    setSkipped: () => {
      const status = get().status;
      // Không hạ cấp khi đã đang nhắc hoặc đã đóng.
      if (status === 'mismatch' || status === 'dismissed') return;
      set({ status: 'skipped' });
    },

    dismiss: () => {
      writeHandled();
      set({ status: 'dismissed', pendingPrefill: null });
    },

    setPendingPrefill: (address) => set({ pendingPrefill: address }),

    consumePendingPrefill: () => {
      const pending = get().pendingPrefill;
      if (pending) set({ pendingPrefill: null });
      return pending;
    },
  })
);

export const useLocationMismatchStore = createSelectors(store);
/** Truy cập store ngoài React (getState/subscribe). */
export const locationMismatchStore = store;
